import React, { useEffect, useRef, useCallback } from 'react';
import { Box, CircularProgress, Typography, useTheme } from '@mui/material';

interface InfiniteScrollProps {
  hasMore: boolean;
  loading: boolean;
  onLoadMore: () => void;
  threshold?: number; // Distance from bottom to trigger load (default: 200px)
  children: React.ReactNode;
  loadingText?: string;
  endMessage?: string;
}

const InfiniteScrollContainer: React.FC<InfiniteScrollProps> = ({
  hasMore,
  loading,
  onLoadMore,
  threshold = 200,
  children,
  loadingText = "Loading more...",
  endMessage = "No more results to load"
}) => {
  const theme = useTheme();
  const containerRef = useRef<HTMLDivElement>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);

  // Handle scroll event with throttling
  const handleScroll = useCallback(() => {
    if (!containerRef.current || loading || !hasMore) {
      return;
    }

    const { scrollTop, scrollHeight, clientHeight } = containerRef.current;
    const distanceFromBottom = scrollHeight - scrollTop - clientHeight;

    if (distanceFromBottom < threshold) {
      onLoadMore();
    }
  }, [loading, hasMore, threshold, onLoadMore]);

  // Set up scroll listener
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    let ticking = false;
    const throttledHandleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          handleScroll();
          ticking = false;
        });
        ticking = true;
      }
    };

    container.addEventListener('scroll', throttledHandleScroll, { passive: true });
    
    return () => {
      container.removeEventListener('scroll', throttledHandleScroll);
    };
  }, [handleScroll]);

  // Alternative: Intersection Observer approach for better performance
  const lastElementRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (loading || !hasMore) return;

    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    observerRef.current = new IntersectionObserver(
      (entries) => {
        const target = entries[0];
        if (target.isIntersecting && hasMore && !loading) {
          onLoadMore();
        }
      },
      {
        threshold: 0.1,
        rootMargin: `${threshold}px 0px ${threshold}px 0px`
      }
    );

    if (lastElementRef.current) {
      observerRef.current.observe(lastElementRef.current);
    }

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [loading, hasMore, threshold, onLoadMore]);

  return (
    <Box
      ref={containerRef}
      sx={{
        height: '100%',
        overflowY: 'auto',
        position: 'relative'
      }}
    >
      {children}
      
      {/* Loading indicator */}
      {loading && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: 2,
            py: 3,
            mt: 2
          }}
        >
          <CircularProgress 
            size={20} 
            sx={{ 
              color: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.6)' 
            }} 
          />
          <Typography 
            variant="body2" 
            sx={{ 
              color: theme.palette.text.secondary,
              fontSize: 14,
              fontWeight: 500
            }}
          >
            {loadingText}
          </Typography>
        </Box>
      )}
      
      {/* Intersection observer target */}
      {hasMore && !loading && (
        <div 
          ref={lastElementRef}
          style={{ 
            height: '10px', 
            background: 'transparent',
            pointerEvents: 'none'
          }} 
        />
      )}
      
      {/* End message */}
      {!hasMore && !loading && (
        <Box
          sx={{
            textAlign: 'center',
            py: 3,
            mt: 2
          }}
        >
          <Typography 
            variant="body2" 
            sx={{ 
              color: theme.palette.text.secondary,
              fontSize: 13,
              opacity: 0.8,
              bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)',
              py: 1.5,
              px: 3,
              borderRadius: 2,
              border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`
            }}
          >
            ✓ {endMessage}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default InfiniteScrollContainer;